<script module lang="ts">
  import type { Snippet } from 'svelte';
  import type { HTMLAttributes } from 'svelte/elements';
  import { LayerHandler } from '../../handlers/LayerHandler';
  import { type AllowedState } from '../content/states.types';

  export const allowedTags = ['section', 'article', 'aside', 'div', 'form'] as const;
  export type AllowedTag = (typeof allowedTags)[number];

  export interface Props extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
    tag?: AllowedTag;
    state?: AllowedState;
    headline?: Snippet;
    actions?: Snippet;
    /** Without a headline the actions get a line of their own instead of lying over the content. */
    actionsLine?: boolean;
    /** Without a headline: at the left end of the line of the actions. */
    lead?: Snippet;
    layerAbs?: boolean;
    allAbs?: boolean;
    lightDarker?: boolean;
    /** Stuck to the bottom of the box (buttons): the box is a column, its content grows, ≥ 1rem above the footer. */
    footer?: Snippet;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { allowedStates } from '../content/states.types';
  import Headline from '../content/Headline.svelte';
  import Content from '../content/Content.svelte';

  const { tag, state: colorState, headline, actions, actionsLine = false, lead, layerAbs, allAbs, lightDarker, footer, children, class: className, ...toNext }: Props = $props();

  // -
  const DefaultTag = allowedTags[0];
  const ElementTag = $derived(tag && allowedTags.includes(tag) ? tag : DefaultTag);

  // -
  const UseState = $derived(colorState && allowedStates.includes(colorState) ? colorState : undefined);
  const UseColorBackground = $derived(UseState ? 'color-' + UseState + '-background' : 'layout-background');
  const UseColorBorder = $derived(UseState ? 'color-' + UseState + '-dominant' : 'layout-background');
  const UseColorShadow = $derived(UseState ? 'color-' + UseState + '-secondary' : undefined);

  // -
  const Layer = new LayerHandler();
  const MyLayer = Layer.upContentLayer();
</script>

<svelte:element
  this={ElementTag}
  {...toNext}
  class={[
    'tagBox',
    'contentLayer',
    `contentLayer${String(MyLayer)}`,
    allAbs && 'contentLayerChildAbs',
    lightDarker && 'lightDarker',
    footer && 'withFooter',
    actions && !headline && !lead && !actionsLine && 'hasStandaloneActions',
    className,
  ]}
  style:--color-background={`var(--${UseColorBackground})`}
  style:--color-border={`var(--${UseColorBorder})`}
  style:--color-shadow={UseColorShadow ? `var(--${UseColorShadow})` : 'transparent'}>
  {#if headline || lead || (actions && actionsLine)}
    <div class="boxHeader">
      {#if headline}
        <Headline state={UseState}>
          {@render headline()}
        </Headline>
      {:else if lead}
        {@render lead()}
      {/if}
      {#if actions}
        <div class="boxActions">
          {@render actions()}
        </div>
      {/if}
    </div>
  {:else if actions}
    <div class="boxActions boxActions--standalone">
      {@render actions()}
    </div>
  {/if}
  {#if children}
    <div class="boxBody contentLayer">
      <Content {children} {layerAbs} />
    </div>
  {/if}
  {#if footer}
    <div class="boxFooter">
      {@render footer()}
    </div>
  {/if}
</svelte:element>

<style lang="scss">
  .tagBox {
    position: relative;
    font-size: var(--font-size);
    background: var(--color-background);
    color: var(--layout-font-color);
    border-radius: 0.5rem;
    padding: calc(var(--inset-abs) * 0.75) var(--inset-abs) var(--font-size) var(--inset-abs);
    border: 1px solid rgb(from var(--color-border) r g b / 25%);
    box-shadow:
      0 1px 3px rgb(from var(--color-shadow) r g b / 15%),
      0 12px 24px -8px rgb(from var(--layout-font-color) r g b / 10%);

    > .boxHeader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      width: 100%;

      > :global(.headLineHeader) {
        flex: 1 1 auto;
        min-width: 0;
      }

      > .boxActions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-shrink: 0;
        margin-left: auto;
      }
    }

    > .boxActions.boxActions--standalone {
      position: absolute;
      top: calc(var(--inset-abs) * 0.75);
      right: var(--inset-abs);
      display: flex;
      align-items: center;
      gap: 0.5rem;
      z-index: 2;
    }

    > .boxHeader + .boxBody {
      margin-top: calc(var(--inset-abs) * 0.75);
    }

    &.withFooter {
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      height: 100%;

      > .boxBody {
        flex: 1 1 auto;
      }

      > .boxFooter {
        margin-top: 1rem;
      }
    }

    &.lightDarker {
      background-image: linear-gradient(var(--layout-light-darker), var(--layout-light-darker));
    }
  }
</style>
